import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { createRole, getRoles, updateRole } from '../../services/roleService';
import { toast } from 'react-toastify';
import './styles/PlatformConfiguration.css';

interface Props {
  nextStep: () => void;
  previousStep: () => void;
}

interface Role {
  id: string;
  title: string;
  description: string;
}

const AVAILABLE_MODULES = ['Compliance', 'Risk Management', 'Governance', 'Controls'];

const RoleSetup = ({ nextStep, previousStep }: Props) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedModules, setSelectedModules] = useState<string[]>(['Compliance', 'Risk Management', 'Governance', 'Controls']);

  const [editingId, setEditingId] = useState<string | null>(null);

  const loadRoles = async () => {
    try {
      const data = await getRoles();

      console.log('Roles:', data);

      setRoles(data);
    } catch (error) {
      console.log('Role Error:', error);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const toggleModule = (module: string) => {
    setSelectedModules((prev) => (prev.includes(module) ? prev.filter((m) => m !== module) : [...prev, module]));
  };

  const handleAddRole = async () => {
    if (!roleName.trim()) return;

    const payload = {
      title: roleName,
      description,
    };

    try {
      if (editingId) {
        await updateRole(editingId, payload);
      } else {
        await createRole(payload);
      }

      await loadRoles();

      setEditingId(null);
      setRoleName('');
      setDescription('');
      setSelectedModules([]);
      toast.success('Role created successfully');
    } catch (error) {
      console.log(error);
      toast.error('Failed to create role');
    }
  };

  return (
    <div className="role-setup-card">
      {/* Header */}
      <div className="role-setup-header">
        <h2 className="role-setup-title">Role Setup</h2>
        <p className="role-setup-subtitle">Define roles and module access</p>
        <p className="role-setup-desc">
          Define the roles in your organization and which modules each role can access. Only activated modules are shown.
        </p>
      </div>

      {/* Existing role cards */}
      <div className="role-cards">
        {roles.map((role) => (
          <div key={role.id} className="role-card">
            <div className="role-card-body">
              <div className="role-card-text">
                <h3 className="role-card-name">{role.title}</h3>
                <p className="role-card-description">{role.description}</p>
                {/*<div className="role-tags">
                  {role.modules.map((mod) => (
                    <span key={mod} className="role-tag">
                      {mod}
                    </span>
                  ))}
                </div>*/}
              </div>
              <button
                type="button"
                className="role-edit-btn"
                onClick={() => {
                  setEditingId(role.id);
                  setRoleName(role.title);
                  setDescription(role.description);
                }}
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add a new role – dashed border */}
      <div className="add-role-section">
        <h3 className="add-role-heading">Add a new role</h3>

        <div className="add-role-row">
          <div className="add-role-field">
            <label className="add-role-label">Role Name</label>
            <input
              type="text"
              className="add-role-input"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder="Role name"
            />
          </div>
          <div className="add-role-field">
            <label className="add-role-label">Description</label>
            <input
              type="text"
              className="add-role-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Manages IT systems"
            />
          </div>
        </div>

        <div className="add-role-modules-block">
          <label className="add-role-label">Module Access</label>
          <div className="role-tags role-tags-selectable">
            {AVAILABLE_MODULES.map((mod) => {
              const selected = selectedModules.includes(mod);
              return (
                <button
                  key={mod}
                  type="button"
                  className={`role-tag ${selected ? 'role-tag-active' : 'role-tag-inactive'}`}
                  onClick={() => toggleModule(mod)}
                >
                  {mod}
                </button>
              );
            })}
          </div>
        </div>

        <button type="button" className="add-role-btn" onClick={handleAddRole}>
          + Add Role
        </button>
      </div>

      {/* Footer */}
      <div className="organization-footer">
        <button onClick={previousStep} className="organization-back-btn">
          <ArrowLeft size={18} />
          Back
        </button>

        <div className="organization-footer-right">
          <button onClick={nextStep} className="organization-next-btn">
            Next
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleSetup;
